import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import LazyImgixImage from '@components/LazyImgixImage';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import useWindowSize from '@hooks/useWindowSize';
import { highlight_path, newsletter_landing_path, subscribe_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { FormLocation } from '@utils/enums';
import { transformToSnakeCase } from '@utils/formatting';
import { getImgixSrc } from '@utils/images';

import { OFFER_PARAM } from '.';
import Button from '../../Button';
import { MORE_SUBMENU_SOCIAL_NAV_ITEMS } from '../../Nav/navItems';

const Dealmaker = 'Dealmaker';
const SUBSCRIBE_TO_DEALMAKER = 'Subscribe to Dealmaker';
interface DealMakerProps {
  dealMakerNewsletterId: number;
  isCurrentUserLoggedIn: boolean;
  isSubscribedToDealMaker: boolean;
}

const DealmakerNewsletter = ({
  dealMakerNewsletterId,
  isCurrentUserLoggedIn,
  isSubscribedToDealMaker,
}: DealMakerProps) => {
  const { trackEvent } = useEventTracking();
  const hydrated = useServerSideRendering();
  const { width: windowWidth } = useWindowSize();

  return (
    <aside className="mx-[calc(-50vw+50%)] flex justify-center bg-grayscale-beige px-16 py-32 lg:px-64 lg:py-48">
      <div className="flex flex-wrap-reverse items-center justify-center gap-24 sm:flex-row sm:flex-nowrap sm:justify-start sm:gap-64 lg:gap-80">
        <div className="w-full max-w-full md:max-w-[417px]">
          <h4 className="my-0 mb-8 max-w-full font-suisse-works text-headline-xxs md:max-w-[366px]">
            Subscribe to Dealmaker, an industry essential resource.
          </h4>
          <div className="mb-24 text-body-lg font-light">
            Venture capital is undergoing massive changes. Award-winning journalist Kate Clark tells
            you what&apos;s coming next for startup founders and investors.
          </div>
          <div className="flex w-full justify-center sm:justify-start ">
            {!isCurrentUserLoggedIn ? (
              hydrated && (
                <div className="w-full">
                  <EmailLeadCapture
                    buttonText="Sign Up"
                    isVertical={windowWidth < 750}
                    buttonClassName="w-1/2 !text-button-md text-white"
                    inputClassName="w-full !text-button-md bg-grayscale-beige"
                    formLocation={FormLocation.FinanceLandingPage}
                    redirectToOnSuccess={subscribe_path({ offer: OFFER_PARAM })}
                    newsletterIds={[dealMakerNewsletterId]}
                  />
                </div>
              )
            ) : !isSubscribedToDealMaker ? (
              <PrimaryButton
                className="!w-[314px] !px-16 !py-8 text-button-sm"
                as="link"
                href={newsletter_landing_path(Dealmaker)}
                onClickCapture={() =>
                  trackEvent(
                    click({
                      clickTarget: ClickTarget.SubscribeNewsletter,
                      clickText: SUBSCRIBE_TO_DEALMAKER,
                    })
                  )
                }
              >
                {SUBSCRIBE_TO_DEALMAKER}
              </PrimaryButton>
            ) : (
              <PrimaryButton
                className="!w-[314px] !px-16 !py-8 text-button-sm"
                as="link"
                href={highlight_path(Dealmaker)}
                onClickCapture={() =>
                  trackEvent(
                    click({
                      clickTarget: ClickTarget.ViewList,
                      clickText: 'Read Dealmaker',
                    })
                  )
                }
              >
                View List
              </PrimaryButton>
            )}
          </div>
          <div className="flex w-full justify-center sm:justify-start">
            <div className="mt-24 flex w-[190px] justify-around gap-24">
              {MORE_SUBMENU_SOCIAL_NAV_ITEMS.map(({ icon: Icon, name, ...rest }) => (
                <Button
                  className="flex h-[21px] w-[21px] items-center justify-center bg-transparent text-grayscale-charcoal  transition-colors hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta"
                  as="externalLink"
                  key={name}
                  onClickCapture={() =>
                    trackEvent(click({ clickTarget: transformToSnakeCase(name) }))
                  }
                  {...rest}
                >
                  <Icon className="h-[21px]" />
                  <span className="sr-only">{name}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex w-full max-w-[460px] justify-center">
          <LazyImgixImage
            src={getImgixSrc({
              path: 'landing_pages/financial_coverages/dealmaker_hero.png?auto=compress,format',
              isEnvDependent: false,
            })}
            sizes="460px"
            alt="DealMaker Newsletter"
            className="h-auto w-full"
          />
        </div>
      </div>
    </aside>
  );
};

export default DealmakerNewsletter;
